import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@/i18n";

export const AuthPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  if (user) {
    navigate("/profile", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "register") {
        const res = await axios.post(
          `${API}/auth/register`,
          formData,
          { withCredentials: true }
        );
        login(res.data);
        toast.success(t('toasts.registerSuccess'));
        navigate("/profile");
      } else {
        const res = await axios.post(
          `${API}/auth/login`,
          { email: formData.email, password: formData.password },
          { withCredentials: true }
        );
        login(res.data);
        toast.success(t('toasts.loginSuccess'));
        navigate("/profile");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toasts.authFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-24 py-12">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#111827] mb-2">
            {mode === "login" ? t('auth.welcomeBack') : t('auth.createAccount')}
          </h1>
          <p className="text-[#6b7280]">
            {mode === "login" ? t('auth.signInSubtitle') : t('auth.signUpSubtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name" className="text-[#111827]">{t('auth.name')}</Label>
              <Input
                id="name"
                data-testid="name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('auth.namePlaceholder')}
                required={mode === "register"}
                className="mt-1"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-[#111827]">{t('auth.email')}</Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('auth.emailPlaceholder')}
              required
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-[#111827]">{t('auth.password')}</Label>
              {mode === "login" && (
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#1a4d2e] hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              )}
            </div>
            <div className="relative mt-1">
              <Input
                id="password"
                data-testid="password-input"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={t('auth.passwordPlaceholder')}
                required
                minLength={6}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#111827]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            data-testid="submit-btn"
            type="submit"
            disabled={loading}
            className="w-full btn-primary rounded-xl py-3 font-semibold"
          >
            {loading ? t('auth.pleaseWait') : mode === "login" ? t('auth.signIn') : t('auth.signUp')}
          </Button>
        </form>

        <p className="text-center text-sm text-[#6b7280] mt-6">
          {mode === "login" ? (
            <>
              {t('auth.noAccount')}{" "}
              <button
                data-testid="switch-to-register-btn"
                onClick={() => setMode("register")}
                className="text-[#1a4d2e] font-medium hover:underline"
              >
                {t('auth.signUp')}
              </button>
            </>
          ) : (
            <>
              {t('auth.hasAccount')}{" "}
              <button
                data-testid="switch-to-login-btn"
                onClick={() => setMode("login")}
                className="text-[#1a4d2e] font-medium hover:underline"
              >
                {t('auth.signIn')}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};
